import { GeoPlace, GeoPlacesApi } from "@/api/geo/GeoPlacesApi";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { useGeoSearchBias } from "@/utils/useGeoSearchBias";
import { useEffect, useState, type Ref } from "react";
import {
    ActivityIndicator,
    Pressable,
    Text,
    TextInput,
    View,
    type TextInputProps,
} from "react-native";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";

/** Ort, ggf. Stadtteil-Kontext, Landkreis, Bundesland und PLZ. */
export function formatPlaceLine(place: GeoPlace): string {
  const parts: string[] = [place.name];
  if (place.placeType === "district" && place.city) parts.push(place.city);
  if (place.county) parts.push(place.county);
  if (place.state) parts.push(place.state);
  if (place.plz) parts.push(place.plz);
  return parts.filter(Boolean).join(", ");
}

function formatPlaceSubtitle(place: GeoPlace): string {
  const parts: string[] = [];
  if (place.placeType === "district" && place.city) parts.push(place.city);
  if (place.county) parts.push(place.county);
  if (place.state) parts.push(place.state);
  if (place.plz) parts.push(`PLZ ${place.plz}`);
  return parts.filter(Boolean).join(" · ") || "—";
}

export function GeoPlaceAutocompleteField(props: {
  valuePlaceId: string | null;
  valueText: string;
  onChangeText: (text: string) => void;
  onSelect: (place: GeoPlace) => void;
  /** Festes Feldlabel. Ohne Angabe bleibt der Placeholder das Label. */
  label?: string;
  placeholder?: string;
  /** Kurzer Hinweis direkt unter dem Feld. */
  hint?: string;
  /** Warnung unter dem Feld, z. B. getippter Ort ohne Vorschlag. */
  warning?: string | null;
  inputRef?: Ref<TextInput>;
  returnKeyType?: TextInputProps["returnKeyType"];
  onSubmitEditing?: TextInputProps["onSubmitEditing"];
  blurOnSubmit?: boolean;
}) {
  const { theme } = useTheme();
  const s = useStyles();
  const geoBias = useGeoSearchBias();
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<GeoPlace[]>([]);
  const [open, setOpen] = useState(false);
  const [focusedIdx, setFocusedIdx] = useState(-1);
  const trimmed = props.valueText.trim();
  const canSearch = trimmed.length >= 2 || /^\d{5}$/.test(trimmed);

  // tiny debounce
  useEffect(() => {
    let alive = true;

    if (!canSearch) {
      setItems([]);
      setBusy(false);
      return;
    }

    setBusy(true);
    const t = setTimeout(() => {
      GeoPlacesApi.search(trimmed, 12, {
        userLat: geoBias?.lat ?? null,
        userLon: geoBias?.lon ?? null,
      })
        .then((res) => {
          if (!alive) return;
          setItems(res);
          setFocusedIdx(-1);
        })
        .catch(() => {
          if (!alive) return;
          setItems([]);
        })
        .finally(() => {
          if (!alive) return;
          setBusy(false);
        });
    }, 250);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [props.valueText, canSearch, geoBias?.lat, geoBias?.lon]);

  return (
    <View style={{ gap: theme.space.xs }}>
      <View style={{ position: "relative" }}>
        <FloatingLabelInput
          ref={props.inputRef}
          label={props.label ?? props.placeholder ?? "Ort oder PLZ (z.B. Chemnitz, 09111)"}
          value={props.valueText}
          returnKeyType={props.returnKeyType}
          blurOnSubmit={props.blurOnSubmit}
          onChangeText={(t) => {
            props.onChangeText(t);
            setOpen(true);
            setFocusedIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyPress={(e) => {
            const key = e.nativeEvent.key;
            if (key === "ArrowDown") {
              setFocusedIdx((i) => Math.min(i + 1, items.length - 1));
            } else if (key === "ArrowUp") {
              setFocusedIdx((i) => Math.max(i - 1, -1));
            } else if (key === "Enter" && focusedIdx >= 0 && focusedIdx < items.length) {
              props.onSelect(items[focusedIdx]);
              setOpen(false);
            }
          }}
          onSubmitEditing={(event) => {
            if (focusedIdx >= 0 && focusedIdx < items.length) {
              props.onSelect(items[focusedIdx]);
              setOpen(false);
              return;
            }
            props.onSubmitEditing?.(event);
          }}
          containerStyle={{ paddingHorizontal: theme.space.md }}
        />

        {busy && (
          <View
            style={{ position: "absolute", right: theme.space.md, top: 12 }}
          >
            <ActivityIndicator />
          </View>
        )}
      </View>

      {open && items.length > 0 && (
        <View
          style={{
            borderWidth: 1,
            borderColor: theme.color.border,
            backgroundColor: theme.color.surface,
            borderRadius: theme.radius.md,
            overflow: "hidden",
          }}
        >
          {items.map((p, idx) => {
            const isFocused = idx === focusedIdx;
            const selectPlace = () => {
              props.onSelect(p);
              setOpen(false);
            };
            return (
              <Pressable
                key={p.id}
                onPressIn={selectPlace}
                onPress={selectPlace}
                style={({ pressed }) => ([
                  s.dividerBottom,
                  {
                    paddingHorizontal: theme.space.md,
                    paddingVertical: theme.space.sm,
                    backgroundColor: isFocused
                      ? theme.color.surfaceHover
                      : pressed
                        ? theme.color.surfaceStrong
                        : "transparent",
                  },
                ])}
              >
                <Text style={{ color: theme.color.text, fontWeight: isFocused ? "900" : "800" }}>
                  {p.name}
                </Text>
                <Text style={{ color: theme.color.muted, marginTop: 2 }}>
                  {formatPlaceSubtitle(p)}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}

      {open && canSearch && !busy && items.length === 0 && (
        <Text style={s.textSmall}>
          Keine Vorschläge gefunden.
        </Text>
      )}

      {props.hint ? (
        <Text
          style={{
            color: theme.color.textSecondary,
            fontSize: theme.fontSize.small,
            marginTop: 2,
          }}
        >
          {props.hint}
        </Text>
      ) : null}
      {props.warning ? (
        <Text
          accessibilityRole="alert"
          style={{
            color: theme.color.warningText,
            fontSize: theme.fontSize.small,
            marginTop: 2,
          }}
        >
          {props.warning}
        </Text>
      ) : null}
    </View>
  );
}
